import React, { useState } from 'react';
import { 
  Box, Tab, Tabs, Typography, Button, Grid, TextField, 
  Paper, Stack, Chip, Container 
} from '@mui/material';
import { 
  FileDownload as DownloadIcon, Payments as FinanceIcon, 
  ReceiptLong as BillingIcon, CalendarMonth as CalendarIcon,
  AutoGraph as AnalyticsIcon, ChevronRight as RightIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { exportCouncilBilling } from '../../api/services/Billing/finance';
import ConfirmModal from '../../components/ConfirmModal';
import BillingHistoryTable from './Table/BillingHistoryTable';
import StaffPayroll from '../../components/Finance/StaffPayroll';

const FinancePage = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({ start_date: '', end_date: '' });

  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: (filterData) => exportCouncilBilling(filterData),
    onSuccess: () => {
      setIsModalOpen(false);
      queryClient.invalidateQueries('billingHistory'); 
    },
    onError: (error) => {
      alert("Export failed: " + error.message);
      setIsModalOpen(false);
    }
  });

  return (
    <Box 
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
        py: 3, px: { xs: 1, md: 3 } 
      }}
    >
      <Container maxWidth={false} sx={{ width: '100%' }}>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', 
                p: 1.5, borderRadius: 3, display: 'flex',
                boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.3)'
              }}>
                <FinanceIcon sx={{ color: '#fff' }} />
              </Box>
            </motion.div>
            <Box>
              <Typography variant="h5" fontWeight="900" color="#1e293b" sx={{ letterSpacing: '-0.02em' }}>Finance Hub</Typography>
              <Typography variant="caption" color="#64748b" fontWeight="600">Real-time Billing & Payroll Management</Typography>
            </Box>
          </Stack>
          <Chip label="2026 Fiscal Cycle" sx={{ fontWeight: 800, bgcolor: '#fff', border: '1px solid #e2e8f0', px: 1 }} />
        </Stack>

        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 5, 
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.7)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ px: 2, pt: 1, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                '& .MuiTab-root': { fontWeight: 800, fontSize: '0.85rem', minHeight: 60 }
              }}
            >
              <Tab label="Council Billing" icon={<BillingIcon sx={{ fontSize: 20 }} />} iconPosition="start" value={1} />
              <Tab label="Staff Payroll" icon={<AnalyticsIcon sx={{ fontSize: 20 }} />} iconPosition="start" value={3} />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <AnimatePresence mode="wait">
              {activeTab === 1 && (
                <motion.div
                  key="billing"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        label="Period Start" type="date" fullWidth size="small"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                        sx={{ bgcolor: '#fff', borderRadius: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        label="Period End" type="date" fullWidth size="small"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                        sx={{ bgcolor: '#fff', borderRadius: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button 
                        variant="contained" 
                        fullWidth 
                        disableElevation
                        startIcon={<DownloadIcon />}
                        onClick={() => filters.start_date && filters.end_date ? setIsModalOpen(true) : alert("Please select dates")}
                        sx={{ 
                          py: 1.2, 
                          borderRadius: 2.5, 
                          fontWeight: 800,
                          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                          '&:hover': { background: '#0f172a' }
                        }}
                      >
                        Generate Export
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <BillingHistoryTable />
                  </Box>
                </motion.div>
              )}

              {activeTab === 3 && (
                <motion.div
                  key="payroll"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Payroll Specific Controls */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={900} color="#1e293b">Payroll Audit Period</Typography>
                      <Typography variant="caption" color="#64748b" fontWeight={600}>Verified staff hours and hourly rate breakdown</Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      disableElevation
                      startIcon={<FinanceIcon />}
                      endIcon={<RightIcon />}
                      sx={{ 
                        borderRadius: 3, fontWeight: 800,
                        background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                        boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
                        '&:hover': { background: '#4338ca' }
                      }}
                    >
                      Authorize Full Pay Run
                    </Button>
                  </Stack>

                  {/* Utmost Clarity Component */}
                  <StaffPayroll />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Paper>
      </Container>

      <ConfirmModal 
        show={isModalOpen} 
        handleClose={() => setIsModalOpen(false)} 
        handleConfirm={() => mutate(filters)} 
        isLoading={isLoading} 
      />
    </Box>
  );
};

export default FinancePage;